<template>
	<main class="auth_theme_container mcp_connect">
		<div class="mcp_card">
			<div class="mcp_handshake" aria-hidden="true">
				<span class="mcp_handshake-brand"><Logo /></span>
				<span class="mcp_handshake-link">
					<span></span><span></span><span></span>
				</span>
				<span class="mcp_handshake-phi">φ</span>
			</div>

			<h1 class="step_title">Autorizar acceso</h1>

			<p v-if="fatalError" class="mcp_error">{{ fatalError }}</p>

			<template v-else-if="!context">
				<p class="step_subtitle">Cargando la solicitud…</p>
				<div class="mcp_skeleton is-wide"></div>
				<div class="mcp_skeleton"></div>
				<div class="mcp_skeleton is-narrow"></div>
			</template>

			<template v-else>
				<p class="step_subtitle">
					<strong>{{ context.client_name || "Un cliente de IA" }}</strong>
					quiere leer los datos financieros de tu cuenta
					(<strong>{{ context.email }}</strong>).
				</p>

				<div class="mcp_scope">
					<span class="mcp_scope-pill">Solo lectura</span>
					<span>
						Reportes y totales agregados. No puede modificar datos ni
						ver documentos individuales.
					</span>
				</div>

				<template v-if="context.companies.length">
					<p class="field_label">
						{{ context.is_analyst
							? "Empresa del acceso (obligatorio)"
							: "Empresa del acceso" }}
					</p>
					<div class="mcp_company_list" role="radiogroup"
						aria-label="Empresa del acceso">
						<label v-for="company in context.companies" :key="company.id"
							class="mcp_company_option"
							:class="{ 'is-selected': selectedCompany === company.id }">
							<input v-model="selectedCompany" type="radio"
								name="mcp_company" :value="company.id" />
							<span class="mcp_company_radio" aria-hidden="true"></span>
							<span class="mcp_company_name">{{ company.name }}</span>
							<span class="mcp_company_rut">{{ company.rut }}</span>
						</label>
						<label v-if="!context.is_analyst && context.companies.length > 1"
							class="mcp_company_option"
							:class="{ 'is-selected': selectedCompany === null }">
							<input v-model="selectedCompany" type="radio"
								name="mcp_company" :value="null" />
							<span class="mcp_company_radio" aria-hidden="true"></span>
							<span class="mcp_company_name">
								Sin empresa fija
							</span>
							<span class="mcp_company_rut">elegir en cada consulta</span>
						</label>
					</div>
				</template>

				<button class="continue_button mcp_submit"
					:disabled="loading || (context.is_analyst && selectedCompany == null)"
					@click="accept">
					{{ loading ? "Autorizando…" : "Autorizar acceso" }}
				</button>

				<p v-if="error" class="mcp_error">{{ error }}</p>

				<p class="mcp_hint">
					Cada conexión nueva revoca la anterior. Para quitar el acceso,
					conecta nuevamente desde tu cliente de IA o pídelo a tu analista.
				</p>
			</template>
		</div>

		<p class="mcp_foot">
			¿Problemas para conectar?
			<a href="mailto:soporte@cforemoto.com">soporte@cforemoto.com</a>
		</p>
	</main>
</template>

<script>
// Página STANDALONE del consent de la Puerta 2 (plan MCP fase 6b): Hydra
// redirige acá con ?consent_challenge=…; el contexto (cliente, email,
// empresas) sale de GET /mcp/consent/context y la aceptación revoca los
// grants previos (un solo grant activo). Analistas eligen UNA empresa
// obligatoria; usuarios finales pueden no fijarla.
//
// Presentación: .auth_theme_container + .mcp_connect (_mcp_connect.scss),
// misma anatomía que /mcp/authorize — sin estilos propios.
import Logo from "@/components/svg/Logo.vue";
import { acceptMcpConsent, getMcpConsentContext } from "@/api/mcpConsent";
import { readOauthChallenge } from "@/utils/oauthChallenge";

export default {
	name: "McpConsent",
	components: { Logo },
	data() {
		return {
			context: null,
			selectedCompany: null,
			loading: false,
			error: "",
			fatalError: "",
		};
	},
	computed: {
		consentChallenge() {
			return readOauthChallenge(this.$route, "consent_challenge");
		},
	},
	async created() {
		if (!this.consentChallenge) {
			this.fatalError = "Falta el parámetro de autorización. Vuelve a iniciar la conexión desde tu cliente de IA.";
			return;
		}
		try {
			const { data } = await getMcpConsentContext(this.consentChallenge);
			this.context = data;
			if (data.companies.length === 1) {
				this.selectedCompany = data.companies[0].id;
			}
		} catch (err) {
			this.fatalError = err?.response?.status === 400
				? "La autorización venció. Vuelve a iniciar la conexión desde tu cliente de IA."
				: "No pudimos cargar la solicitud. Intenta de nuevo.";
		}
	},
	methods: {
		redirect(url) {
			// Método propio para poder espiarlo en los specs.
			window.location.assign(url);
		},
		async accept() {
			this.loading = true;
			this.error = "";
			try {
				const { data } = await acceptMcpConsent(
					this.consentChallenge, this.selectedCompany,
				);
				this.redirect(data.redirect_to);
			} catch (err) {
				const status = err?.response?.status;
				if (status === 403) {
					this.error = "Tu cuenta no tiene habilitado el acceso a Phi.";
				} else if (status === 400) {
					this.error = "No pudimos autorizar con esos datos. Revisa la empresa elegida o reinicia la conexión.";
				} else {
					this.error = "No pudimos procesar la autorización. Intenta de nuevo.";
				}
				this.loading = false;
			}
		},
	},
};
</script>
